import { useState } from 'react';
import { PersonButton } from '@/features/people/PersonDrawer';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { BarChart3, Bell, Check, Download, EyeOff, Lock, MoreHorizontal, Timer } from 'lucide-react';
import { ApiError, type Message, type PollAnswers, type PollQuestion, type PollQuestionResult, type PollView } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { relative } from '@/lib/format';
import { queryClient } from '@/lib/queryClient';
import { realtime } from '@/lib/realtime';
import { useUi } from '@/lib/store';
import { Avatar, Button, Menu, MenuItem, Modal, Spinner, Textarea, ask } from '@/components/ui';
import { downloadText, ukDateTime } from './pollUtils';

export const pollKey = (id: string) => ['poll', id] as const;

export function usePoll(id: string | null | undefined) {
  return useQuery({
    queryKey: pollKey(id ?? ''), queryFn: () => api.poll(id!), enabled: Boolean(id),
    // Without Supabase Realtime (local dev) refresh open polls so results move.
    refetchInterval: q => (realtime.mode === 'polling' && q.state.data && !q.state.data.is_closed ? 10_000 : false),
  });
}

const pct = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);
const ago = (iso: string) => (Date.now() - Date.parse(iso) < 60_000 ? 'just now' : relative(iso));

/** Horizontal bar per option (single / multi / rating). */
export function ResultBars({ q, r, mine, compact }: { q: PollQuestion; r: PollQuestionResult; mine?: PollAnswers[string]; compact?: boolean }) {
  const top = Math.max(0, ...r.options.map(o => o.count));
  const picked = (i: number) => (Array.isArray(mine) ? mine.includes(i) : q.kind === 'rating' ? Number(mine) === Number(r.options[i].label) : mine === i);
  return (
    <div className="space-y-1.5" role="list" aria-label={`Results: ${q.prompt}`}>
      {r.options.map((o, i) => (
        <div key={o.label} role="listitem" className="relative overflow-hidden rounded-md border border-slate-200 dark:border-slate-700">
          <div className={clsx('absolute inset-y-0 left-0 transition-all', o.count === top && top > 0 ? 'bg-accent-soft' : 'bg-slate-100 dark:bg-slate-800')}
            style={{ width: `${pct(o.count, r.answered)}%` }} />
          <div className={clsx('relative flex items-center gap-2 px-2.5', compact ? 'py-1' : 'py-1.5')}>
            <span className="min-w-0 flex-1 truncate text-sm">
              {q.kind === 'rating' && q.scale_labels && i === 0 && q.scale_labels[0] ? `${o.label} - ${q.scale_labels[0]}`
                : q.kind === 'rating' && q.scale_labels && i === r.options.length - 1 && q.scale_labels[1] ? `${o.label} - ${q.scale_labels[1]}` : o.label}
              {picked(i) && <Check className="ml-1 inline h-3.5 w-3.5 text-accent-fg" aria-label="your answer" />}
            </span>
            {o.voters && o.voters.length > 0 && (
              <span className="flex -space-x-1.5" title={o.voters.map(v => v.display_name).join(', ')}>
                {o.voters.slice(0, 4).map(v => <Avatar key={v.user_id} id={v.user_id} name={v.display_name} size={18} />)}
                {o.voters.length > 4 && <span className="ml-2 text-[11px] text-slate-500">+{o.voters.length - 4}</span>}
              </span>
            )}
            <span className="w-16 shrink-0 text-right text-xs tabular-nums text-slate-500"><b className="text-slate-700 dark:text-slate-200">{pct(o.count, r.answered)}%</b> · {o.count}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function QuestionResults({ q, r, mine, compact, maxTexts = 3 }: { q: PollQuestion; r: PollQuestionResult; mine?: PollAnswers[string]; compact?: boolean; maxTexts?: number }) {
  if (q.kind === 'text') {
    const texts = r.texts ?? [];
    return (
      <div className="space-y-1.5">
        {texts.length === 0 && <div className="text-sm text-slate-500">No answers yet</div>}
        {texts.slice(0, maxTexts).map((t, i) => (
          <div key={i} className="rounded-md bg-slate-50 px-2.5 py-1.5 text-sm dark:bg-slate-800/60">
            <span className="whitespace-pre-wrap">“{t.text}”</span>
            {t.display_name && <span className="ml-1.5 text-xs text-slate-500">- {t.display_name}</span>}
          </div>
        ))}
        {texts.length > maxTexts && <div className="text-xs text-slate-500">+{texts.length - maxTexts} more answers</div>}
      </div>
    );
  }
  return (
    <div>
      {q.kind === 'rating' && (
        <div className="mb-1.5 flex items-baseline gap-1.5">
          <span className="text-2xl font-bold tabular-nums">{r.average ?? '-'}</span>
          <span className="text-sm text-slate-500">average on a {q.scale_min ?? 1}-{q.scale_max ?? 5} scale · {r.answered} answer{r.answered === 1 ? '' : 's'}</span>
        </div>
      )}
      <ResultBars q={q} r={r} mine={mine} compact={compact} />
    </div>
  );
}

/** Input for one question. */
export function QuestionInput({ q, value, onChange, error }: { q: PollQuestion; value: PollAnswers[string] | undefined; onChange: (v: PollAnswers[string] | undefined) => void; error?: string }) {
  if (q.kind === 'single' || q.kind === 'multi') {
    const multi = q.kind === 'multi';
    const sel = (i: number) => (multi ? Array.isArray(value) && value.includes(i) : value === i);
    const toggle = (i: number) => {
      if (!multi) return onChange(value === i ? undefined : i);
      const cur = Array.isArray(value) ? value : [];
      onChange(cur.includes(i) ? cur.filter(x => x !== i) : [...cur, i]);
    };
    return (
      <div role={multi ? 'group' : 'radiogroup'} aria-label={q.prompt} className="space-y-1.5">
        {q.options.map((o, i) => (
          <button key={o} type="button" role={multi ? 'checkbox' : 'radio'} aria-checked={sel(i)} onClick={() => toggle(i)}
            className={clsx('flex w-full items-center gap-2.5 rounded-md border px-2.5 py-1.5 text-left text-sm transition',
              sel(i) ? 'border-accent-line bg-accent-soft text-accent-fg' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800')}>
            <span className={clsx('flex h-4 w-4 shrink-0 items-center justify-center border', multi ? 'rounded' : 'rounded-full', sel(i) ? 'border-accent-line bg-accent text-on-accent' : 'border-slate-300 dark:border-slate-600')}>
              {sel(i) && (multi ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-white" />)}
            </span>
            {o}
          </button>
        ))}
        {multi && <div className="text-xs text-slate-500">{q.max_choices ? `Pick up to ${q.max_choices}` : 'Pick any that apply'}</div>}
        {error && <div className="text-xs text-rose-600">{error}</div>}
      </div>
    );
  }
  if (q.kind === 'rating') {
    const pts: number[] = [];
    for (let p = q.scale_min ?? 1; p <= (q.scale_max ?? 5); p++) pts.push(p);
    return (
      <div>
        <div role="radiogroup" aria-label={q.prompt} className="flex flex-wrap gap-1.5">
          {pts.map(p => (
            <button key={p} type="button" role="radio" aria-checked={value === p} onClick={() => onChange(value === p ? undefined : p)}
              className={clsx('h-9 w-9 rounded-md border text-sm font-semibold tabular-nums transition', value === p ? 'border-accent-line bg-accent text-on-accent' : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800')}>{p}</button>
          ))}
        </div>
        {q.scale_labels && (
          <div className="mt-1 flex justify-between text-[11px] text-slate-500" style={{ width: `${pts.length * 2.625 - 0.375}rem` }}><span>{q.scale_labels[0]}</span><span>{q.scale_labels[1]}</span></div>
        )}
        {error && <div className="text-xs text-rose-600">{error}</div>}
      </div>
    );
  }
  return (
    <div>
      <Textarea rows={2} aria-label={q.prompt} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value || undefined)} placeholder="Your answer" />
      {error && <div className="text-xs text-rose-600">{error}</div>}
    </div>
  );
}

const fieldErrors = (e: unknown): Record<string, string> =>
  e instanceof ApiError && e.body && typeof e.body === 'object' && 'errors' in e.body ? (e.body as { errors: Record<string, string> }).errors ?? {} : {};

function Badges({ p }: { p: PollView }) {
  const chip = 'inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  return (
    <>
      {p.anonymous && <span className={chip} title="Nobody - including the person who started it - can see who answered what"><EyeOff className="h-3 w-3" />Anonymous</span>}
      {p.results_visibility === 'after_close' && !p.is_closed && <span className={chip}><Lock className="h-3 w-3" />Results after close</span>}
      {p.is_closed
        ? <span className={clsx(chip, 'bg-slate-700 text-white dark:bg-slate-600')}>Closed</span>
        : p.closes_at && <span className={chip}><Timer className="h-3 w-3" />Closes {ukDateTime(p.closes_at)}</span>}
    </>
  );
}

/** Poll / survey card rendered in place of the message body. */
export function PollCard({ m }: { m: Message }) {
  const pollId = (m.metadata as { poll_id?: string } | undefined)?.poll_id ?? null;
  const q = usePoll(pollId);
  const toast = useUi(s => s.toast);
  const [answers, setAnswers] = useState<PollAnswers>({});
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [full, setFull] = useState(false);
  if (!pollId) return null;
  if (q.isLoading) return <Spinner className="h-4 w-4" />;
  if (!q.data) return <div className="text-sm text-slate-500">This poll is no longer available.</div>;
  const p = q.data;
  const answering = p.can_respond && (!p.my_answers || editing);
  const set = (data: PollView) => queryClient.setQueryData(pollKey(p.id), data);
  const submit = async () => {
    setBusy(true); setErrors({});
    try { set(await api.respondPoll(p.id, answers)); setEditing(false); }
    catch (e) { setErrors(fieldErrors(e)); toast(errorMessage(e), 'error'); }
    finally { setBusy(false); }
  };
  const act = async (fn: () => Promise<PollView | { reminded: number; poll: PollView }>, ok?: (r: unknown) => string) => {
    setMenu(false);
    try { const r = await fn(); set('poll' in r ? r.poll : r); if (ok) toast(ok(r), 'success'); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const exportCsv = async () => {
    setMenu(false);
    try { downloadText(await api.pollCsv(p.id), `${p.title.replace(/[^\w-]+/g, '-').slice(0, 40)}-results.csv`); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const startEdit = () => { setAnswers(p.my_answers ?? {}); setEditing(true); };
  const many = p.questions.length > 1 || p.kind === 'survey';
  const visibleQs = !answering && many ? p.questions.slice(0, 2) : p.questions;

  return (
    <div className="mt-1 max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid="poll-card">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 bg-slate-50/80 px-3.5 py-2 dark:border-slate-800 dark:bg-slate-800/50">
        <BarChart3 className="h-4 w-4 text-accent-fg" />
        <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{p.kind === 'survey' ? 'Survey' : 'Poll'}</span>
        <Badges p={p} />
        {p.can_manage && (
          <span className="ml-auto">
            <Menu open={menu} setOpen={setMenu} trigger={<button title="Poll options" aria-label="Poll options" className="rounded p-1 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"><MoreHorizontal className="h-4 w-4" /></button>}>
              {p.can_see_results && <MenuItem icon={<BarChart3 className="h-4 w-4" />} onClick={() => { setMenu(false); setFull(true); }}>View full results</MenuItem>}
              {!p.is_closed && <MenuItem icon={<Bell className="h-4 w-4" />} onClick={() => void act(() => api.remindPoll(p.id), r => `Reminder sent to ${(r as { reminded: number }).reminded} ${(r as { reminded: number }).reminded === 1 ? 'person' : 'people'}`)}>Remind people who haven't answered</MenuItem>}
              {p.can_see_results && <MenuItem icon={<Download className="h-4 w-4" />} onClick={() => void exportCsv()}>Export CSV</MenuItem>}
              {!p.is_closed && <MenuItem danger icon={<Lock className="h-4 w-4" />} onClick={() => { setMenu(false); void ask('Close this poll? Nobody will be able to answer after this.').then(ok => { if (ok) return act(() => api.closePoll(p.id)); }); }}>Close now</MenuItem>}
            </Menu>
          </span>
        )}
      </div>
      <div className="space-y-3 px-3.5 py-3">
        <div>
          <div className="font-semibold text-slate-900 dark:text-slate-50">{p.title}</div>
          {p.description && <div className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">{p.description}</div>}
        </div>
        {visibleQs.map((qq, i) => {
          const r = p.results?.find(x => x.question_id === qq.id);
          return (
            <div key={qq.id}>
              {many && <div className="mb-1.5 text-sm font-medium">{i + 1}. {qq.prompt}{answering && !qq.required && <span className="ml-1 text-xs font-normal text-slate-500">(optional)</span>}</div>}
              {answering
                ? <QuestionInput q={qq} value={answers[qq.id]} onChange={v => setAnswers(a => { const n = { ...a }; if (v === undefined) delete n[qq.id]; else n[qq.id] = v; return n; })} error={errors[qq.id]} />
                : r ? <QuestionResults q={qq} r={r} mine={p.my_answers?.[qq.id]} compact />
                  : p.my_answers ? <div className="text-sm text-slate-500">Your answer: {answerText(qq, p.my_answers[qq.id])}</div> : null}
            </div>
          );
        })}
        {!answering && many && p.questions.length > 2 && (
          <button className="text-sm font-medium link" onClick={() => setFull(true)}>
            {p.can_see_results ? `See all ${p.questions.length} questions and results` : `See all ${p.questions.length} questions`}
          </button>
        )}
        {!answering && p.my_answers && !p.can_see_results && (
          <div className="rounded-md bg-emerald-50 px-2.5 py-1.5 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
            Thanks - your answer is in. Results are shown when the {p.kind} closes.
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-3.5 py-2 text-xs text-slate-500 dark:border-slate-800">
        <span><b className="text-slate-700 dark:text-slate-200">{p.response_count}</b> of {p.eligible_count} answered</span>
        {p.my_responded_at && <span>· you answered {ago(p.my_responded_at)}</span>}
        {p.respond_blocked_reason && !p.my_answers && <span>· {p.respond_blocked_reason}</span>}
        {answering && !editing && p.can_see_results && p.response_count > 0 && <button className="font-medium link" onClick={() => setFull(true)}>View results</button>}
        <span className="ml-auto flex gap-1.5">
          {answering && editing && <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>}
          {answering && <Button size="sm" loading={busy} onClick={() => void submit()} disabled={Object.keys(answers).length === 0}>{p.my_answers ? 'Update answer' : many ? 'Submit answers' : 'Vote'}</Button>}
          {!answering && p.my_answers && p.can_respond && p.allow_change && (
            <>
              <Button size="sm" variant="ghost" onClick={() => void act(() => api.withdrawPollResponse(p.id))}>Withdraw</Button>
              <Button size="sm" variant="secondary" onClick={startEdit}>Change answer</Button>
            </>
          )}
        </span>
      </div>
      {full && <PollResultsModal poll={p} onClose={() => setFull(false)} onExport={() => void exportCsv()} />}
    </div>
  );
}

function answerText(q: PollQuestion, a: PollAnswers[string] | undefined) {
  if (a === undefined) return '-';
  if (q.kind === 'single') return q.options[Number(a)];
  if (q.kind === 'multi') return (a as number[]).map(i => q.options[i]).join(', ');
  return String(a);
}

/** Full results with charts, respondents (named polls, managers) and CSV export. */
export function PollResultsModal({ poll: p, onClose, onExport }: { poll: PollView; onClose: () => void; onExport: () => void }) {
  return (
    <Modal open wide onClose={onClose} title={<span className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-accent-fg" />{p.title}</span>}
      footer={<>{p.can_see_results && <Button variant="secondary" onClick={onExport}><Download className="h-4 w-4" />Export CSV</Button>}<Button onClick={onClose}>Done</Button></>}>
      <div className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
        <span><b>{p.response_count}</b> of {p.eligible_count} answered ({pct(p.response_count, p.eligible_count)}%)</span>
        <span>· started by <PersonButton userId={p.created_by} name={p.created_by_name} className="rounded hover:underline">{p.created_by_name}</PersonButton> {ago(p.created_at)}</span>
        <Badges p={p} />
      </div>
      <div className="space-y-5">
        {p.questions.map((q, i) => {
          const r = p.results?.find(x => x.question_id === q.id);
          return (
            <section key={q.id}>
              <h3 className="mb-2 text-sm font-semibold">{p.questions.length > 1 ? `${i + 1}. ` : ''}{q.prompt}
                <span className="ml-2 text-xs font-normal text-slate-500">{r ? `${r.answered} answer${r.answered === 1 ? '' : 's'}` : ''}</span></h3>
              {r ? <QuestionResults q={q} r={r} mine={p.my_answers?.[q.id]} maxTexts={50} /> : <div className="text-sm text-slate-500">Results are shown when the {p.kind} closes.</div>}
            </section>
          );
        })}
      </div>
      {p.non_respondents && (
        <div className="mt-6 grid gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2 dark:border-slate-800">
          <div>
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Answered ({p.respondents?.length ?? 0})</h4>
            <ul className="space-y-1 text-sm">{p.respondents?.map(r => <li key={r.user_id} className="flex items-center gap-2"><Avatar size={20} id={r.user_id} name={r.display_name} />{r.display_name}<span className="text-xs text-slate-500">{ago(r.responded_at)}</span></li>)}</ul>
          </div>
          <div>
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Not answered yet ({p.non_respondents.length})</h4>
            <ul className="space-y-1 text-sm">{p.non_respondents.map(r => <li key={r.user_id} className="flex items-center gap-2"><Avatar size={20} id={r.user_id} name={r.display_name} />{r.display_name}</li>)}</ul>
            {p.last_reminder_at && <div className="mt-1.5 text-xs text-slate-500">Last reminder {ago(p.last_reminder_at)}</div>}
          </div>
        </div>
      )}
      {p.anonymous && p.can_manage && <p className="mt-4 text-xs text-slate-500">Anonymous: individual answers and who has answered are not shown to anyone.</p>}
    </Modal>
  );
}
